import { logout } from '../../utils'
import { getCurrentUser, signOut } from '@lastbrand/fe-common/universal-auth'
import { isB2BSelfServeDomain } from '@src/utils/b2b/b2b.utils'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { getCookieStorage } from '@src/utils/enhancedCookie'

const validHostNames = new Set([
  'quince.com',
  'www.quince.com',
  'checkout.quince.com',
  'onequince.com',
  'www.onequince.com',
  'checkout.onequince.com',
])

const checkoutHostNames = new Set(['checkout.quince.com', 'checkout.onequince.com'])

/**
 *
 * @param {URL} url
 * @returns string | null
 */
export function isValidQuinceUrl(url) {
  return (
    validHostNames.has(url.host) ||
    validHostNames.has(url.hostname) ||
    url.hostname === 'localhost' ||
    // The below ones are to enable for staging/prod preview.
    url.origin.endsWith('.quince.com') ||
    url.origin.endsWith('.onequince.com')
  )
}

export function isCheckoutUrl(urlString) {
  try {
    const url = new URL(urlString)
    return (
      checkoutHostNames.has(url.host) ||
      checkoutHostNames.has(url.hostname) ||
      url.origin.endsWith('checkout.quince.com') ||
      url.origin.endsWith('checkout.onequince.com')
    )
  } catch {
    return false
  }
}

/**
 * Returns true if the URL is a checkout URL (subdomain or same-origin checkout path).
 * Used to decide whether to rewrite redirect URL to current locale after login (region switch).
 */
export function isCheckoutRedirectUrl(urlString) {
  try {
    if (isCheckoutUrl(urlString)) return true
    if (globalThis.window === undefined) return false
    const url = new URL(urlString)
    return url.origin === globalThis.window.location.origin && url.pathname.includes('/checkout')
  } catch (err) {
    // Invalid URL or missing origin; treat as non-checkout redirect
    if (err instanceof Error) {
      console.warn('isCheckoutRedirectUrl:', err.message)
    }
    return false
  }
}

export function getSanitizedRedirectUrls() {
  try {
    const searchParams = new URLSearchParams(location.search)
    const redirecturi = searchParams.get('redirecturi') ?? searchParams.get('returnUrl')
    const redirectUrl = new URL(redirecturi)
    if (isValidQuinceUrl(redirectUrl)) {
      return redirectUrl.toString()
    }
  } catch (e) {
    // this will be invoked at SSR. Don't remove
  }
  return null
}

export const logoutIfCustomerIdIsUnavailable = async () => {
  try {
    const currentUser = await getCurrentUser()
    if (!currentUser.attributes['custom:customer_id']) {
      localStorage.setItem('Side:Toast:Error', 'Login failed. For assistance, please contact us.')
      await signOut()
      getCookieStorage().removeCookieLocalized(ANALYTIC_COOKIES.LB_CUSTOMER_ID, { isGlobal: true })
      getCookieStorage().removeCookieLocalized(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL, { isGlobal: true })
      logout(() => {})
      return true
    }
    return false
  } catch (error) {
    console.error('Error while fetching user data ', error)
    return true
  }
}

export const globalSignOut = async () => {
  try {
    await signOut({ global: true })
    logout(() => {})
  } catch (error) {
    console.error('Error during global sign out:', error)
  }
}

export function isB2BReturnUrl(urlString) {
  try {
    const url = new URL(urlString)
    return isB2BSelfServeDomain(url.hostname)
  } catch {
    return false
  }
}

/**
 * True when the current page URL has a `?code=` param, which Cognito sets when redirecting
 * back from a Google OAuth round-trip. SSR-safe (returns false when window is unavailable).
 *
 * @returns {boolean}
 */
export function isOAuthCallbackOnUrl() {
  if (typeof window === 'undefined') return false
  // eslint-disable-next-line i18next/no-literal-string -- OAuth query-param key, not user-facing copy
  return new URLSearchParams(window.location.search).has('code')
}

// @i18n-ignore
export const SESSION_EXPIRED_KEY = 'session-expired'
export const LOGIN_STATUS_MESSAGE = {
  [SESSION_EXPIRED_KEY]: `Your session has expired.\nPlease login again to continue.`,
}
